import React, { useState } from 'react'
import PropTypes from 'prop-types'

import { StatusNotification } from '@aok/ui-elements'

import { DisabledState, ImageShape, ImageType, QuizType } from '../M047Constants'
import defaultProps from '../../../../utils/propTypes/defaultProps'

import M047ImagePerQuestion from '../M047ImagePerQuestion/M047ImagePerQuestion'
import M047QuestionLayout from '../M047QuestionLayout/M047QuestionLayout'
import M047RadioButtonGroup from '../M047RadioButtonGroup/M047RadioButtonGroup'

const M047SingleSelection = ({
  navigateToNextPage,
  navigateToPreviousPage,
  onChangeCallback,
  headline,
  image,
  icon,
  options,
  solutionText,
  settings,
  quizType,
}) => {
  const showImagePerQuestion =
    !settings?.imagePerAnswer &&
    ((image && settings?.imageType === ImageType.Picture) || (icon && settings?.imageType === ImageType.Icon))
  const showImagePerAnswer = settings?.imagePerAnswer && options.every((option) => option.image !== undefined)

  const defaultSelection = options?.find((option) => option.selected === true)?.id
  const hasSelectedValue = !!defaultSelection

  const correctAnswer = options?.find((option) => isCorrectAnswer(option))?.text
  const userAnsweredCorrectly = options?.find((option) => option.selected && isCorrectAnswer(option))

  const [showFeedback, setShowFeedback] = useState(!!options?.find((option) => !!option?.disabledState) ?? false) // if there is a disabledState, the user has already answered the question

  const radioButtonData = options.map((option) => {
    return { value: option.id, label: option.text, disabledState: option.disabledState, image: option.image }
  })

  const handleRadioChange = (event) => {
    // Sets selected attribute to the option. On the final screen there can be filtered after the selected attribute
    const updatedOptions = options.map((option) => {
      // check if one of the options is selected
      return { ...option, selected: event.target.value === option?.id }
    })

    onChangeCallback(updatedOptions)
  }

  const handleNextButtonClick = () => {
    if (quizType === QuizType.Selbsttest || (quizType === QuizType.WahrFalsch && showFeedback)) {
      navigateToNextPage()
      return
    }

    checkAnswer()
    setShowFeedback(true)
  }

  function checkAnswer() {
    const updatedOptions = options.map((option) => {
      let disabledState
      let wasSelected = option.selected

      if (isCorrectAnswer(option)) {
        disabledState = DisabledState.Success
      } else if (wasSelected) {
        disabledState = DisabledState.Error
      } else {
        disabledState = DisabledState.ErrorMuted
      }

      // check if one of the options is selected
      return { ...option, disabledState }
    })

    onChangeCallback(updatedOptions)
  }

  function isCorrectAnswer(option) {
    return option?.weight > 0
  }

  return (
    <M047QuestionLayout
      headline={headline}
      onNextButtonClick={handleNextButtonClick}
      onPreviousButtonClick={navigateToPreviousPage}
      nextButtonDisabled={!hasSelectedValue}
      nextButtonLabel={quizType === QuizType.WahrFalsch && !showFeedback ? 'Antworten' : 'Weiter'}
    >
      <div className="flex flex-col 600:items-center 600:flex-row 600:gap-x-16 720:gap-x-24 1000:gap-x-32">
        {/* Image Container */}
        {showImagePerQuestion && (
          <div
            className={`w-full shrink-0 ${
              settings?.imageType === ImageType.Icon || settings?.imageShape === ImageShape.Circle
                ? '600:w-160'
                : '600:w-1/2'
            }`}
          >
            <M047ImagePerQuestion {...settings} image={image} icon={icon} />
          </div>
        )}

        {/* Radiobuttons Container */}
        <div className="flex-grow 600:self-center">
          <M047RadioButtonGroup
            groupName="m047-single-selection"
            groupLabel={headline?.text}
            radioButtons={radioButtonData}
            defaultSelection={defaultSelection}
            onChange={handleRadioChange}
            disabled={showFeedback}
            showImagePerOption={showImagePerAnswer}
            className={`${showImagePerQuestion ? 'mt-28 600:mt-0' : ''}`}
            buttonOuterClasses={` ${
              showImagePerQuestion
                ? 'flex flex-wrap gap-16'
                : showImagePerAnswer
                ? `grid 600:grid-cols-2 ${
                    radioButtonData.length > 2 ? '840:grid-cols-3' : '1000:grid-cols-3'
                  }  gap-24 600:gap-y-40 1000:gap-x-32`
                : 'grid grid-cols-1 600:grid-cols-2 gap-16'
            }`}
          />
        </div>
      </div>

      {/* Feedback for Wahr/Falsch quizzes */}
      {quizType === QuizType.WahrFalsch && showFeedback && (
        <div role="region" aria-live="polite">
          <StatusNotification
            showIcon={false}
            theme="light"
            message={
              userAnsweredCorrectly ? (
                <p>
                  <span className="font-semibold">Ihre Antwort ist richtig.</span>
                  <br></br>
                  {solutionText}
                </p>
              ) : (
                <p>
                  <span className="font-semibold">Ihre Antwort ist leider falsch.</span>
                  <br></br>
                  <br></br>
                  <b>Richtige Antwort: {correctAnswer}</b>
                  <br></br>
                  {solutionText}
                </p>
              )
            }
            isSimpleText={false}
            variant={userAnsweredCorrectly ? 'success' : 'info'}
            className="mt-32 600:mt-40 textBlock"
          />
        </div>
      )}
    </M047QuestionLayout>
  )
}

M047SingleSelection.propTypes = {
  navigateToNextPage: PropTypes.func,
  navigateToPreviousPage: PropTypes.func,
  onChangeCallback: PropTypes.func,
  headline: PropTypes.shape({
    tag: PropTypes.string,
    text: PropTypes.string,
  }),
  image: PropTypes.shape({
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        breakpoint: PropTypes.number,
      })
    ),
    alt: PropTypes.string,
  }),
  icon: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      weight: PropTypes.number,
      image: PropTypes.shape({
        variants: PropTypes.arrayOf(
          PropTypes.shape({
            url: PropTypes.string,
            breakpoint: PropTypes.number,
          })
        ),
        alt: PropTypes.string,
      }),
      selected: PropTypes.bool,
    })
  ),
  solutionText: PropTypes.string,
  settings: PropTypes.shape({
    imagePerAnswer: PropTypes.bool,
    imageType: PropTypes.oneOf(Object.values(ImageType)),
    imageShape: PropTypes.oneOf(Object.values(ImageShape)),
  }),
  quizType: PropTypes.oneOf(Object.values(QuizType)),
}

M047SingleSelection.defaultProps = {
  navigateToNextPage: () => {},
  navigateToPreviousPage: () => {},
  onChangeCallback: () => {},
  headline: {
    text: defaultProps.HEADLINE,
  },
  options: [],
}

export default M047SingleSelection
